import {endDate, startDate, ymd, getDateInTimezone} from '../../src/utils/dates.ts';
import {div, span} from '../../src/utils/html.ts';
import {slugify} from '../../src/utils/strings.ts';
import type {
	ArticleCardLayoutRecord,
	DigitalEditionItemData,
	ImageVersion,
	Item,
	ListItemData,
	NodeChunk,
	Page,
	ReelData,
	ReelSource
} from '../../types/types.ts';
import {LoadResourceError} from '../lib/errors.ts';
import type {LayoutParser} from '../lib/layout-parser.ts';
import type {Store} from '../lib/store.ts';
import {Article, type ArticleData, type BylineData, type RelatedArticle} from '../models/article.ts';
import type {ContentSourceEdition} from '../models/content-source-edition.ts';
import type {ReaderItem} from '../models/reader-edition.ts';
import {Reel} from '../models/reel.ts';
import {createChunk} from '../pages/reader/lib/markup.ts';
import {loadNordjyskeArticle, loadNordjyskeArticles} from '../services/content.ts';
import {ContentAdapter} from './content-adapter.ts';

export class NordjyskeContentAdapter extends ContentAdapter {
	readonly locale = 'da-DK';
	readonly timezone = 'Europe/Copenhagen';
	singleItemSourceId = '43'; // Deafult channelId

	getSectionForPrintItem(printPage: Page, item: Item, parser: LayoutParser) {
		const printPageSection = printPage.properties?.folioCode;

		return String(parser.getSection() || printPageSection || item.data.section || item.data.category || '');
	}

	getStoryForPrintItem(_printPage: Page, item: Item, parser: LayoutParser): string {
		return parser.getFirstParagraphLeadin() || item.data.category || '';
	}

	loadItem(
		article: Article,
		_groupBy: string | null,
		_sortOrder: number | undefined,
		_store: Store
	): Promise<Article | null> {
		return Promise.resolve(article);
	}
	createEditionBrief(
		_data: DigitalEditionItemData,
		_store: Store,
		_articleIdsInEditiond: string[],
		_editionTitle: string
	): Article {
		throw new Error('Method not implemented.');
	}

	getListQueryParams(_publishDate: string) {
		return {};
	}

	async loadArticleById(articleId: string, store: Store) {
		const response = await loadNordjyskeArticle(articleId);
		if (response.status !== 200) {
			return null;
		}

		return new Article(
			normalize(response.body, this.singleItemSourceId),
			store.getItemState(`${this.singleItemSourceId}:${articleId}`)
		);
	}

	async loadPlaylist(id: string) {
		const response = await fetch(
			`https://cdn.jwplayer.com/v2/playlists/${id}?format=json&internal=false&page_offset=1&page_limit=10`
		);
		const data: JwPlaylistResponse = await response.json();

		return data;
	}

	async loadReels(): Promise<ReelData[]> {
		const data = await this.loadPlaylist('z9qYRYGx');
		return data.playlist.map((item) => this.formatReel(item));
	}

	formatReel(item: JwPlaylistItem) {
		if (item['dnmh-article-url']) {
			// console.log(item);
		}

		const relatedArticleId = extractArticleIdFromUrl(item['dnmh-article-url']);
		return {
			id: item.mediaid,
			title: item.title,
			description: item.description,
			tags: !item.tags
				? []
				: [item.tags].map((tag) => {
						return {
							displayName: tag,
							urlPattern: slugify(tag)
						};
					}),
			article: relatedArticleId
				? {
						id: relatedArticleId,
						url: `/article/${relatedArticleId}`,
						remoteUrl: item['dnmh-article-url'],
						label: item['dnmh-link-text']
					}
				: undefined,
			publishedAt: new Date(item.pubdate * 1000).toISOString(),
			manifest: item.sources.find(({type}) => type === 'application/vnd.apple.mpegurl'),
			sources: item.sources
				.filter((source) => source.type === 'video/mp4')
				.map((source: JwVideoSource) => {
					return {
						file: source.file,
						type: source.type,
						label: source.label,
						width: source.width,
						height: source.height,
						target: source.width <= 720 ? 'mobile' : 'desktop'
					} as ReelSource;
				}),
			posterUrl: item.image,
			sourceUrl: `https://nordjyske.dk/reel/${item.mediaid}`
		};

		function extractArticleIdFromUrl(url: string) {
			if (!url) {
				return null;
			}
			const lastPart = url.split('/').at(-1);
			if (lastPart && /^\d+$/.test(lastPart)) {
				return lastPart;
			}

			return null;
		}
	}

	async loadArticleList(edition: ContentSourceEdition, store: Store) {
		const editionDate = new Date(edition.date);
		const editionDateYMD = ymd(editionDate, this.timezone);
		const articlesResponse = await loadNordjyskeArticles(
			startDate(editionDate, this.timezone),
			endDate(editionDate, this.timezone),
			edition.query?.params
		);
		if (articlesResponse.status !== 200) {
			throw new LoadResourceError('Error loading content query source', articlesResponse);
		}

		const reels = await this.loadReels();

		return [
			...articlesResponse.body.map((item) => {
				return {
					data: {
						title: item.storyContent.heading,
						type: 'nordjyske-article',
						...item
					}
				};
			}),
			...(await Promise.all(
				reels
					.filter((reelData) => ymd(reelData.publishedAt, this.timezone) === editionDateYMD)
					.map(async (reelData) => {
						const reel = new Reel(reelData, store.getItemState(reelData.id));
						if (reel.article?.id) {
							const article = await this.loadArticleById(reel.article.id, store);
							if (article) {
								reel.relatedArticle = article;
							}
						}

						return {data: reel};
					})
			))
		];
	}

	async loadRelatedArticles(_article: Article, _sourceId?: string): Promise<RelatedArticle[]> {
		return [];
	}

	formatListItems(
		items: (ListItemData | Reel)[],
		_groupBy: string | null,
		date: string | null,
		store: Store,
		sourceId?: string,
		timezone?: string
	): ReaderItem[] {
		const list: ReaderItem[] = [];
		const currentSourceId = sourceId || this.singleItemSourceId;

		items.forEach((item) => {
			if (item instanceof Reel) {
				list.push(item);
				return;
			}
			const articleData = normalize(item as unknown as NordjyskeArticleData, currentSourceId);
			const article = new Article(articleData, store.getItemState(`${currentSourceId}:${articleData.id}`));

			if (
				!article ||
				(date && getDateInTimezone(article.publishedAt, timezone || 'Europe/Copenhagen') !== date)
			) {
				return;
			}

			list.push(article);
		});

		return list;
	}

	async loadSingleItem(articleId: string, sourceId: string | undefined, store: Store): Promise<Article> {
		const currentSourceId = sourceId || this.singleItemSourceId;
		const articleDataResponse = await loadNordjyskeArticle(articleId);
		if (articleDataResponse.status !== 200) {
			throw new LoadResourceError(
				`Could not load article ${articleId} from source Nordjyske article endpoint`,
				articleDataResponse
			);
		}

		const normalizedData = normalize(articleDataResponse.body, currentSourceId);
		const storeKey = `${currentSourceId}:${articleId}`;
		return new Article(normalizedData, store.getItemState(storeKey));
	}

	loadSingleItemFromFixture(data: string, store: Store): Article {
		// This is only used in test suites, so it's ok to throw an error if data isn't parsable
		const fixture = JSON.parse(data) as NordjyskeArticleData;
		const currentSourceId = 'fixture';
		const normalizedData = normalize(fixture, currentSourceId);
		const storeKey = `${currentSourceId}:${fixture.id}`;
		return new Article(normalizedData, store.getItemState(storeKey));
	}
}

function normalize(data: NordjyskeArticleData, sourceId: string): ArticleData {
	const section = getSection();
	const chunks = createContentGroups();

	return {
		id: data.id,
		objectId: null,
		contentGroups: chunks,
		publishedAt: data.storyMeta.firstPublishedDate,
		sort: Date.parse(data.storyMeta.firstPublishedDate),
		groupBy: '',
		section,
		sourceId,
		data,
		layout: {
			html: ''
		},
		byline: {
			html: renderBylines(),
			items: normalizeBylines(data.storyMeta.bylines)
		},
		cardLayouts: data.cardLayouts,
		tags: data.storyMeta.tags?.map((tag) => ({
			displayName: tag.name,
			urlPattern: tag.urlFriendlyName
		})),
		relatedArticles: [], // Will be loaded just-in-time
		pretitle: data.storyContent.tagline || '',
		title: data.storyContent.heading,
		intro: data.storyContent.subHeading,
		size: calculateSize(chunks),
		story: getStory(),
		asset: {
			thumbnailUrl: getLeadAssetThubnailUrl()
		},
		sourceUrl: `https://nordjyske.dk${data.dnmhMeta.channelMeta[0].relativeUrl}`
	};

	function getLeadAssetThubnailUrl() {
		return data.storyContent.mainImage?.versions?.find(({width}) => width > 200)?.url;
	}

	function calculateSize(chunks: NodeChunk[]) {
		let figuresCount = 0;
		const chars = chunks.reduce((acc, chunk) => {
			figuresCount += chunk.figures.length;
			return acc + chunk.chars;
		}, 0);

		return chars + Math.min(figuresCount, 3) * 250;
	}

	function getStory() {
		if (data.storyMeta.articleTypeId === 89) {
			return '';
		}
		return data.storyMeta.tags?.[0]?.name || section;
	}

	function getSection() {
		return data.storyMeta.categories.find(({rank}) => rank === 1)?.name || '';
	}

	function normalizeBylines(bylines: NordjyskeByline[]): BylineData[] {
		return bylines.map((byline) => {
			if (byline.author) {
				return {
					name: byline.author.name,
					email: byline.author.email,
					label: byline.author.jobTitle,
					photoUrl: byline.author.image,
					bio: byline.author.text
				};
			}
			return {
				name: byline.customName ?? undefined,
				label: byline.label ?? undefined,
				bio: byline.customDescription ?? undefined
			};
		});
	}

	function renderBylines() {
		if (!data.storyMeta.bylines) {
			return '';
		}
		return div(
			data.storyMeta.bylines
				.map((byline, index) => {
					const author = byline.author;
					if (author) {
						return `
<div class="byline--web">
	${author.image ? div(`<img src="${author.image}"/>`, 'byline__photo') : ''}
	<div class="byline__text">
		${div(author.name, 'byline__name')}
		${div(author.email, 'byline__email')}
		${div(author.jobTitle, 'byline__title')}
	</div>
</div>
`;
					}

					if (
						(byline.customDescription && byline.customName) ||
						index + 1 === data.storyMeta.bylines.length
					) {
						return `
<div class="byline--web">
	<div class="byline__text">
		${div(byline.customName, 'byline__customName')}
		${div(byline.customDescription, 'byline__customDescription')}
		${div(byline.label, 'byline__label')}
	</div>
</div>
`;
					}

					return '';
				})
				.join(''),
			`byline bylines--${data.storyMeta.bylines.length}`
		);
	}

	function createContentGroups(): NodeChunk[] {
		let figuresIndex = 1;

		const firstPageChunk = getFirstPageChunk();
		const bodyChunks = getBodyChunks();

		return [firstPageChunk, ...bodyChunks];

		function getBodyChunks() {
			if (!data.storyContent.elements.length) {
				return [];
			}

			const tempEl = document.createElement('div');
			const bodyElements = processBodyElements(data.storyContent.elements);

			return assignElementsToChunks(bodyElements);

			function processBodyElements(elements: NordjyskeElement[]) {
				return elements
					.map((el) => {
						switch (el.type) {
							case 0:
								tempEl.innerHTML = createImageMarkup(el);
								if (!tempEl.firstElementChild?.firstElementChild) {
									return null;
								}
								tempEl.firstElementChild.firstElementChild.classList.add('figure');
								return tempEl.firstElementChild.firstElementChild;
							case 1:
								tempEl.innerHTML = createImageGalleryMarkup(el);
								return tempEl.firstElementChild;
							case 2:
								// TODO: This prevents overly long word/word/words from breaking pressflow
								tempEl.innerHTML = el.text.replace(/(\w)\/(\w)/g, '$1/ $2').replaceAll('<br />', '\n');
								return tempEl.firstElementChild;

							case 3:
								tempEl.innerHTML = `<h3>${el.text}</h3>`;
								// TODO: Consider checking for following images and adding page breaks
								// tempEl.firstElementChild?.classList.add('page-break-before');
								return tempEl.firstElementChild;

							case 13:
								tempEl.innerHTML = createPdfMarkup(el);
								return tempEl.firstElementChild;
							case 7:
								tempEl.innerHTML = createQuoteMarkup(el);
								return tempEl.firstElementChild;

							case 6:
								// Læs også
								return null;

							case 8:
								tempEl.innerHTML = createInfoboxMarkup(el);
								return tempEl.firstElementChild;

							case 9:
								tempEl.innerHTML = createVideoMarkup(el);
								return tempEl.firstElementChild;

							case 4: {
								// TODO: We now render these a single card elements, consider adapting to content
								renderCodeElement(el, tempEl);
								return tempEl.firstElementChild;
							}

							case 14:
								// Empty element
								return null;

							default:
								// @ts-expect-error
								console.log('Unhandled type', data.id, el.type, el);
								return null;
						}
					})
					.filter((el) => el) as HTMLElement[];
			}
		}

		function getFirstPageChunk() {
			const firstPageChunk = createChunk();
			const leadAssetMarkup = createImageMarkup(data.storyContent.mainImage);

			const leadAssetTempEl = document.createElement('div');
			leadAssetTempEl.innerHTML = leadAssetMarkup;
			firstPageChunk.figures.push(
				...Array.from(leadAssetTempEl.children)
					.map((el) => {
						if (!el.firstElementChild) {
							return null;
						}
						el.firstElementChild.classList.add('figure', `figure-${figuresIndex++}`);
						return el.firstElementChild;
					})
					.filter((el) => el instanceof HTMLElement)
			);

			return firstPageChunk;
		}

		function createImageMarkup(image: NordjyskeMainImage | NordjyskeImageElement) {
			if (!image) {
				return '';
			}

			const size = image.width >= 1024 ? 'large' : 'small';
			const format = getFormat(image.width, image.height);

			let captionHTML = '';

			if (image.description || image.credit) {
				captionHTML = div(
					`${span(image.description, 'caption')} ${image.credit ? span(image.credit, 'credit') : ''}`,
					'caption-wrapper'
				);
			}

			const srcset = (image.versions || [])
				.filter(({format}) => format === 'original')
				.map((image) => `${image.url} ${image.width}w`)
				.join(', ');

			return `
<div>
	<div class="picture picture--${format} picture--${size}">
			<div class="image-wrapper" style="--asset-aspect-ratio: ${image.width} / ${image.height}">
			<img src="${image.imageUrl}" srcset="${srcset}" ${getAoiAttributes(image)}>
			</div>
	        ${captionHTML}
    </div>
</div>
			`;
			function getAoiAttributes(image: NordjyskeMainImage | NordjyskeImageElement) {
				if (!image.aoi) {
					return '';
				}

				// Convert AOI coordinates to percentages (0-100) for responsive rendering
				return `
					 data-aoi-x-percent="${(image.aoi.x / image.width) * 100}"
					 data-aoi-y-percent="${(image.aoi.y / image.height) * 100}"
					 data-aoi-width-percent="${(image.aoi.width / image.width) * 100}"
					 data-aoi-height-percent="${(image.aoi.height / image.height) * 100}"
					 data-aoi-origin="${image.aoi.origin}"
				`;
			}
		}

		function createQuoteMarkup(quote: NordjyskeQuoteElement) {
			return `
<figure class="quote">
	<blockquote class="quote-text">
		${quote.text}
	</blockquote>
	<figcaption class="quote-creator">${quote.creator}</figcaption>
</figure>`;
		}

		function createPdfMarkup(pdf: NordjyskePdfElement) {
			if (!pdf.imageUrl) {
				return '';
			}

			let captionHTML = '';

			if (pdf.description || pdf.credit) {
				captionHTML = div(
					`${span(pdf.description, 'caption')} ${pdf.credit ? span(pdf.credit, 'credit') : ''}`,
					'caption-wrapper'
				);
			}

			return `
<div>
	<a href="${pdf.imageUrl}" target="_blank" class="picture picture--pdf">
		<div class="image-wrapper"><img src="${pdf.imageUrl}" alt="${pdf.filename}"></div>
		${captionHTML}
	</a>
</div>`;
		}

		function createImageGalleryMarkup(gallery: NordjyskeImageGalleryElement) {
			const imagesHtml = gallery.images
				.map((image) => createImageMarkup(image as unknown as NordjyskeImageElement))
				.join('');

			return `<div class="image-gallery">
				${imagesHtml}
			</div>`;
		}

		function createVideoMarkup(video: NordjyskeVideoElement) {
			const poster = video.videoImages.find((image) => image.isPrimary)?.src;
			const hlsSource = video.videoSources.find((source) => source.type === 'application/vnd.apple.mpegurl');

			if (!hlsSource) {
				return '';
			}

			return `
<figure class="video-player">
	<div class="video-wrapper">
		<video controls poster="${poster}" src="${hlsSource.file}"></video>
	</div>
	<figcaption class="figcaption">
		${div(video.title, 'video-title')}
		${div(video.description, 'video-description')}
	</figcaption>
</figure>`;
		}

		function renderCodeElement(el: NordjyskeCodeElement, tempEl: HTMLElement) {
			if (el.originalUrl?.includes('https://infogram.com/')) {
				tempEl.innerHTML = `<iframe src="${el.originalUrl}"></iframe>`;
				tempEl.firstElementChild?.classList.add('page-break-before');
				tempEl.firstElementChild?.classList.add('page-break-after');
				return;
			}

			if (el.code) {
				// tempEl.innerHTML = el.code;
				tempEl.innerHTML = el.code.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
			}
			const contentEl = tempEl.firstElementChild as HTMLDivElement;
			if (contentEl) {
				const source = contentEl.dataset.source;
				if (source) {
					contentEl.innerHTML = `<iframe src="${contentEl.dataset.source}"></iframe>`;
					tempEl.firstElementChild?.classList.add('page-break-before');
					tempEl.firstElementChild?.classList.add('page-break-after');
				}
			}
		}

		function createInfoboxMarkup(infobox: NordjyskeInfoboxElement) {
			const elementsHtml = infobox.elements.map((element) => element.code).join('');

			return `
<aside class="infobox">
	<h4 class="infobox-title">${infobox.title}</h4>
	<div class="infobox-content">
		${elementsHtml}
	</div>
	${infobox.source ? `<div class="infobox-source">Kilde: ${infobox.source}</div>` : ''}
</aside>`;
		}

		function assignElementsToChunks(updatedElements: HTMLElement[]) {
			const chunks: NodeChunk[] = [];

			let currentChunk = createChunk();
			chunks.push(currentChunk);
			updatedElements.forEach((el: HTMLElement) => {
				if (el.classList.contains('page-break-before')) {
					if (currentChunk.nodes.length) {
						currentChunk = createChunk([el]);
						chunks.push(currentChunk);
					} else {
						currentChunk.nodes.push(el);
					}

					if (el.classList.contains('page-break-after')) {
						currentChunk = createChunk();
						chunks.push(currentChunk);
					}

					return;
				}

				if (el.classList.contains('figure')) {
					if (currentChunk.figures.length > 0) {
						currentChunk = createChunk();
						chunks.push(currentChunk);
					}
					el.classList.add(`figure-${figuresIndex++}`);
					currentChunk.figures.push(el);
				} else {
					currentChunk.nodes.push(el);
					currentChunk.chars = currentChunk.chars + el.innerText.length;
				}
			});

			return chunks.filter((chunk) => chunk.nodes.length || chunk.extraNodes[0].length || chunk.figures.length);
		}
	}
}

function getFormat(width: number, height: number) {
	const imageHeightRatio = height / width;
	if (imageHeightRatio < 0.75) {
		return 'landscape';
	}

	if (imageHeightRatio < 0.9) {
		return '4_3';
	}
	if (imageHeightRatio < 1.1) {
		return 'square';
	}
	if (imageHeightRatio < 1.34) {
		return '3_4';
	}

	return 'portrait';
}

type JwPlaylistResponse = {
	title: string;
	description: string;
	kind: string;
	feedid: string;
	links: {
		first: string;
		last: string;
		next?: string;
	};
	playlist: JwPlaylistItem[];
	feed_instance_id: string;
};

type JwPlaylistItem = {
	title: string;
	mediaid: string;
	link: string;
	image: string;
	images: JwImage[];
	feedid: string;
	duration: number;
	content_source_type: string;
	pubdate: number;
	updated: number;
	description: string;
	tags: string;
	sources: JwSource[];
	tracks: JwTrack[];
	variations: Record<string, unknown>;
	'dnmh-article-url': string;
	'dnmh-author': string;
	'dnmh-link-text': string;
	'dnmh-media-copyright': string;
	'dnmh-media-format': string;
	'dnmh-media-internal-description': string;
	'dnmh-media-native': string;
	'dnmh-media-published-timestamp': string;
	'dnmh-media-status': string;
	'dnmh-media-type': string;
	'dnmh-product': string;
};

type JwImage = {
	src: string;
	width: number;
	type: 'image/jpeg' | 'video/mp4';
};

type JwSource =
	| JwVideoSource
	| JwAudioSource
	| {
			file: string;
			type: 'application/vnd.apple.mpegurl';
	  };

type JwVideoSource = {
	file: string;
	type: 'video/mp4';
	height: number;
	width: number;
	label: string;
	bitrate: number;
	filesize: number;
	framerate: number;
};

type JwAudioSource = {
	file: string;
	type: 'audio/mp4';
	label: 'AAC Audio';
	bitrate: number;
	filesize: number;
};

type JwTrack = {
	file: string;
	kind: 'thumbnails';
};

export type NordjyskeArticleData = {
	pk: string;
	id: string;
	storyVersion: number;
	dnmhMeta: NordjyskeDnmhMeta;
	storyMeta: NordjyskeStoryMeta;
	storyContent: NordjyskeStoryContent;
	cardLayouts?: {
		desktop: ArticleCardLayoutRecord;
		mobile: ArticleCardLayoutRecord;
	};
};

type NordjyskeChannelMeta = {
	channel: number;
	publishDate: string;
	relativeUrl: string;
};

type NordjyskeDnmhMeta = {
	syncDate: string;
	source: 'aptoma';
	revision: number;
	patches: unknown[];
	storyGuid: string;
	firstPublishDate: string;
	channelMeta: NordjyskeChannelMeta[];
};

type NordjyskeCategory = {
	name: string;
	rank: number;
	qcode: string;
};

type NordjyskeTag = {
	name: string;
	urlFriendlyName: string;
	qcode: string;
	type: string;
	sameAs: string;
	isPrimary: boolean;
};

type NordjyskeAuthor = {
	id: string | null;
	username: string;
	name: string;
	email: string;
	text: string;
	phone: string;
	twitter: string | null;
	jobTitle: string;
	image: string;
};

type NordjyskeByline = {
	label: string | null;
	customName: string | null;
	customDescription: string | null;
	author: NordjyskeAuthor;
};

type NordjyskeMetaTagAttribute = {
	type: number;
	value: string;
};

type NordjyskeMetaTag = {
	attributes: NordjyskeMetaTagAttribute[];
};

type NordjyskeDistributionProperty = {
	id: number;
	datatype: string;
	value: string;
	name: string;
};

type NordjyskeDistribution = {
	id: number;
	status: string;
	scheduled: string | null;
	channel: {
		id: number;
		name: string;
		label: string;
	};
	properties: NordjyskeDistributionProperty[];
};

type NordjyskeStoryMeta = {
	publishingStatus: number;
	firstPublishedDate: string;
	createDate: string;
	modifiedDate: string;
	productionStatus: string;
	publicationName: string;
	mainArticleId: number;
	source: string | null;
	articleTypeId: number;
	charCount: number;
	wordCount: number;
	imageCount: number;
	videoCount: number;
	categories: NordjyskeCategory[];
	tags: NordjyskeTag[];
	authors: NordjyskeAuthor[];
	bylines: NordjyskeByline[];
	lastEditBy: string;
	paywall: boolean;
	isVideoTeaser: boolean;
	isStaticVideoTeaser: boolean;
	isNoIndex: boolean;
	isLive: boolean;
	isBreaking: boolean;
	topLayout: string;
	allowComments: boolean;
	cxenseRecommendableStart: string | null;
	cxenseRecommendableEnd: string | null;
	facebookPixelId: string | null;
	googleTagManagerId: string | null;
	linkedInInsightTagId: string | null;
	priority: string;
	highlighted: boolean;
	metaTags: NordjyskeMetaTag[];
	distribution: NordjyskeDistribution[];
};

type NordjyskeFocalPoint = {
	x: number;
	y: number;
};

type NordjyskeAoi = {
	height: number;
	width: number;
	x: number;
	y: number;
	focus: NordjyskeFocalPoint;
	origin: string;
};

export type NordjyskeBaseImage = {
	imageUrl: string;
	height: number;
	width: number;
	versions?: ImageVersion[];
};

export type NordjyskeMainImage = NordjyskeBaseImage & {
	type: 0;
	headline: string;
	description: string;
	credit: string;
	byline: string;
	// Note: 'focualPoint' is a typo in the source data
	focualPoint: NordjyskeFocalPoint;
	aoi: NordjyskeAoi;
	filename: string;
};

type NordjyskeTextElement = {
	type: 2;
	text: string;
};

type NordjyskeHeadingElement = {
	type: 3;
	text: string;
};

type NordjyskePdfElement = {
	type: 13;
	imageUrl: string;
	description: string;
	credit: string;
	byline: string;
	filename: string;
};

export type NordjyskeGalleryImage = NordjyskeBaseImage & {
	headline: string;
	description: string | null;
	credit: string | null;
	byline: string | null;
	focualPoint: NordjyskeFocalPoint;
	aoi: {
		height: number;
		width: number;
		x: number;
		y: number;
		origin: string;
	};
	filename: string;
};

type NordjyskeImageGalleryElement = {
	type: 1;
	images: NordjyskeGalleryImage[];
	headline: string;
	description: string;
	byline: string;
};

type NordjyskeRelatedStoryElement = {
	type: 6;
	storyId: number;
	ocStoryId: string;
	queryString: string | null;
	image: NordjyskeMainImage;
	paywall: boolean;
	mainCategory: string;
	primaryTag: string;
	channelMeta: NordjyskeChannelMeta[];
	heading: string;
};

type NordjyskeInfoboxElement = {
	type: 8;
	title: string;
	elements: NordjyskeCodeElement[];
	source: string;
};

type NordjyskeCodeElement = {
	type: 4;
	code: string;
	label: string | null;
	originalUrl: string | null;
	previewImage: string | null;
	title: string;
};

type NordjyskeEmptyElement = {
	type: 14;
};

export type NordjyskeVideoImage = {
	src: string;
	width: number;
	type: string;
	isPrimary: boolean;
	versions?: ImageVersion[];
};

type NordjyskeVideoSource = {
	file: string;
	type: string;
	height: number | null;
	width: number | null;
	label: string | null;
	bitrate: number | null;
	filesize: number | null;
	framerate: number | null;
};

type NordjyskeVideoCustomParameters = {
	dnmhArticleUrl: string;
	dnmhAuthor: string;
	dnmhLinkText: string;
	dnmhMediaCopyright: string;
	dnmhMediaFormat: string;
	dnmhMediaInternalDescription: string;
	dnmhMediaNative: string;
	dnmhMediaPublishedTimestamp: string;
	dnmhMediaStatus: string;
	dnmhMediaType: string;
	dnmhProduct: string;
};

type NordjyskeVideoElement = {
	type: 9;
	id: string;
	title: string;
	provider: number;
	description: string;
	tags: unknown[];
	duration: number;
	publishDate: string;
	videoImages: NordjyskeVideoImage[];
	videoSources: NordjyskeVideoSource[];
	customParameters: NordjyskeVideoCustomParameters;
};

export type NordjyskeImageElement = NordjyskeBaseImage & {
	type: 0;
	headline: string;
	description: string;
	credit: string;
	byline: string;
	// Note: 'focualPoint' is a typo in the source data
	focualPoint: NordjyskeFocalPoint;
	aoi: NordjyskeAoi;
	filename: string;
};

type NordjyskeQuoteElement = {
	type: 7;
	text: string;
	creator: string;
};

export type NordjyskeElement =
	| NordjyskeTextElement
	| NordjyskeImageGalleryElement
	| NordjyskeHeadingElement
	| NordjyskePdfElement
	| NordjyskeCodeElement
	| NordjyskeRelatedStoryElement
	| NordjyskeInfoboxElement
	| NordjyskeEmptyElement
	| NordjyskeVideoElement
	| NordjyskeImageElement
	| NordjyskeQuoteElement
	| NordjyskeMainImage;

export type NordjyskeImageType = NordjyskeMainImage | NordjyskeImageElement;

type NordjyskeStoryContent = {
	heading: string;
	subHeading: string;
	tagline: string | null;
	mainImage: NordjyskeMainImage;
	mainVideo: null;
	elements: NordjyskeElement[];
	businessProfile: null;
	vigeurPartnerProfile: null;
	summary: null;
	shareText: string | null;
	seoFields: null | {
		pageTitle: string | null;
		metaDescription: string | null;
	};
};
